import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

// Where Wallet Plug sends the customer back after the hosted checkout. The
// `status` query param is just a UX hint — the trusted result comes from asking
// our backend to verify the payment (which calls Wallet Plug's verify endpoint).
// On success we poll a few times because the processor may settle a beat after
// the redirect.

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 6;

export default function PaymentReturnPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const hint = params.get('status') || 'success';

  // confirming | pending | failed | cancelled | error
  const [state, setState] = useState(hint === 'success' ? 'confirming' : hint);
  const attempts = useRef(0);
  const timer = useRef(null);

  const confirm = useCallback(async () => {
    try {
      // Two ways the order becomes paid: (1) our best-effort verify-on-return,
      // or (2) Wallet Plug's IPN webhook flipping it server-side. Check both —
      // whichever wins, advance to the thank-you page.
      const { data } = await api.post(`/orders/${id}/walletplug/confirm`);
      let paid = data.paid;
      if (!paid) {
        const { data: order } = await api.get(`/orders/${id}`);
        paid = ['paid', 'processing', 'shipped', 'delivered'].includes(order.status);
      }
      if (paid) {
        navigate(`/thank-you/${id}`, { replace: true });
        return;
      }
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) { setState('pending'); return; }
      timer.current = setTimeout(confirm, POLL_INTERVAL_MS);
    } catch {
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) { setState('error'); return; }
      timer.current = setTimeout(confirm, POLL_INTERVAL_MS);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (hint === 'success') confirm();
    return () => clearTimeout(timer.current);
  }, [hint, confirm]);

  const body = {
    confirming: {
      title: 'Confirming your payment…',
      text: 'Hang tight — we’re verifying your payment with the processor.',
      spinner: true,
      cta: null,
    },
    pending: {
      title: 'Payment is processing',
      text: 'Your payment hasn’t fully cleared yet. We’ll email your confirmation as soon as it does — no need to pay again.',
      cta: { to: '/account', label: 'View my orders' },
    },
    failed: {
      title: 'Payment failed',
      text: 'Your payment didn’t go through. You can try again from checkout.',
      cta: { to: '/cart', label: 'Back to cart' },
    },
    cancelled: {
      title: 'Payment cancelled',
      text: 'You cancelled the payment. Your cart is still saved if you’d like to try again.',
      cta: { to: '/cart', label: 'Back to cart' },
    },
    error: {
      title: 'Couldn’t confirm automatically',
      text: 'We couldn’t confirm the payment status right now. If you completed payment, it will be reflected on your orders shortly.',
      cta: { to: '/account', label: 'View my orders' },
    },
  }[state] || {};

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-lg mx-auto px-4 py-20 w-full text-center">
        {body.spinner && (
          <div className="mx-auto mb-6 h-10 w-10 rounded-full border-4 border-tea-200 border-t-tea-600 animate-spin" />
        )}
        <h1 className="text-2xl font-bold mb-3">{body.title}</h1>
        <p className="text-gray-600 mb-8">{body.text}</p>
        {body.cta && (
          <Link to={body.cta.to} className="btn-primary inline-block px-6 py-2">
            {body.cta.label}
          </Link>
        )}
      </main>
      <Footer />
    </div>
  );
}
